import { when } from 'mobx';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';
import type { IIntegrationUIFieldOption } from '@feathr/components';
import type { ICrmMappingDraft } from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.hooks';
import {
  useCrmMappingDraft,
  useCrmMappingsRefresh,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmMappings.hooks';
import { useStore } from '@feathr/extender/state';

import { useLazyOptions } from '../useLazyOptions';
import { getFeathrFieldOptions, RAISERS_EDGE_MAPPING_RULES } from './RaisersEdgeMappingsTab.utils';

export type IRaisersEdgeMappingDraft = ICrmMappingDraft;

/**
 * Mapping edits staged for the page's Save & Sync. Reads the integration's mapping store; the
 * Mappings tab owns its load. Lives on the page so staged edits survive a tab switch.
 */
export function useRaisersEdgeMappingDraft(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
): IRaisersEdgeMappingDraft {
  return useCrmMappingDraft(integration, 'Blackbaud', RAISERS_EDGE_MAPPING_RULES);
}

/** Reloads the saved mappings on each poll, so each row's last sync stays current. */
export function useRaisersEdgeMappingsRefresh(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
  pollCount: number,
): void {
  useCrmMappingsRefresh(integration, pollCount);
}

/** Feathr person fields a mapping can target, loaded the first time `enabled` is true. */
export function useFeathrFieldOptions(
  enabled: boolean,
): readonly IIntegrationUIFieldOption[] | undefined {
  const { t } = useTranslation();
  const { CustomFields } = useStore();

  async function load(): Promise<IIntegrationUIFieldOption[]> {
    const customFields = CustomFields.list({
      filters: { collection: FieldCollection.Person, is_archived__ne: true },
      pagination: { page_size: 1000 },
    });
    await when(() => !customFields.isPending);
    if (customFields.isErrored) {
      throw customFields.error;
    }
    return getFeathrFieldOptions(customFields.models);
  }

  return useLazyOptions({
    enabled,
    errorLabel: (error) => t('Failed to load Feathr fields:\n{{- error}}', { error }),
    load,
  });
}
